<div class="view-container">
  <mat-card *authGroup="['SuperAdmin', 'OperationsManager']" class="user-view-card">
    @if (user) {
      <div class="user-view-card__header">
        <div class="user-view-card__title-row">
          <div class="user-view-card__icon">
            <mat-icon>person</mat-icon>
          </div>
          <div>
            <h1 class="user-view-card__title">{{ getAttribute('name', user) }}</h1>
            <p class="user-view-card__subtitle">{{ getAttribute('email', user) }}</p>
          </div>
        </div>

        @if (perm.canMutate()) {
          <div class="user-view-card__header-actions">
            <button
              type="button"
              mat-stroked-button
              class="user-view-card__header-btn"
              (click)="resetPassword()"
            >
              Reset password
            </button>
            <button
              type="button"
              (click)="toggleEditMode()"
              mat-flat-button
              color="primary"
              class="user-view-card__edit-btn"
            >
              {{ editMode ? 'Details' : 'Edit' }}
            </button>
          </div>
        }
      </div>
    }

    <div class="user-view-card__actions">
      <button type="button" mat-stroked-button class="user-view-card__action-btn" (click)="backToList()">
        <mat-icon>arrow_back</mat-icon>
        Back to list
      </button>
    </div>

    @if (user && !editMode) {
      <div class="user-view-card__details details">
        <table>
          <tr>
            <th>Email</th>
            <td>{{ getAttribute('email', user) || '-' }}</td>
          </tr>
          <tr>
            <th>Phone number</th>
            <td>{{ getAttribute('phone_number', user) || '-' }}</td>
          </tr>
          <tr>
            <th>Role</th>
            <td>
              <mat-chip-set>
                @for (role of currentRoles; track role) {
                  <mat-chip>{{ role }}</mat-chip>
                }
              </mat-chip-set>
            </td>
          </tr>
          <tr>
            <th>Company / Individual</th>
            <td>
              <mat-chip-set>
                @for (company of companies; track company) {
                  <mat-chip>{{ company }}</mat-chip>
                }
              </mat-chip-set>
            </td>
          </tr>
        </table>
      </div>
    }

    @if (editMode) {
      <form [formGroup]="updateUserForm" class="user-view-card__form" (ngSubmit)="save()">
        <div class="row g-2">
          <div class="col-md-6">
            <p>
              <mat-form-field appearance="outline">
                <mat-label>Name</mat-label>
                <input formControlName="name" type="text" matInput />
                @if (updateUserForm.get('name')?.invalid) {
                  <mat-error>Name is required</mat-error>
                }
              </mat-form-field>
            </p>

            <p>
              <mat-form-field appearance="outline">
                <mat-label>Email</mat-label>
                <input readonly formControlName="email" type="email" matInput />
                @if (updateUserForm.get('email')?.invalid) {
                  <mat-error>Email is required</mat-error>
                }
              </mat-form-field>
            </p>

            <p>
              <mat-form-field appearance="outline" floatLabel="always" class="user-view-card__company-field">
                <mat-label>Company or individual</mat-label>
                <div class="user-view-card__company-row">
                  <mat-button-toggle-group
                    name="linkType"
                    [value]="linkType"
                    (change)="setLinkType($event.value)"
                  >
                    <mat-button-toggle value="company">Company</mat-button-toggle>
                    <mat-button-toggle value="individual">Individual</mat-button-toggle>
                  </mat-button-toggle-group>
                  <mat-chip-grid #companyList aria-label="Company or individual selection" class="user-view-card__company-search">
                    @for (company of companies; track company) {
                      <mat-chip-row [removable]="removable" (removed)="removeCompany(company)">
                        {{ company }}
                        @if (removable) {
                          <button matChipRemove type="button">
                            <mat-icon>cancel</mat-icon>
                          </button>
                        }
                      </mat-chip-row>
                    }
                    @if (linkType === 'company') {
                      <input
                        #companyInput
                        [placeholder]="companies.length > 0 ? '' : 'Search companies...'"
                        formControlName="companySearch"
                        [matAutocomplete]="companyAuto"
                        [matChipInputFor]="companyList"
                        [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                        (matChipInputTokenEnd)="addCompany($event)"
                      />
                    }
                    @if (linkType === 'individual') {
                      <input
                        #individualInput
                        [placeholder]="companies.length > 0 ? '' : 'Search individuals...'"
                        formControlName="individualSearch"
                        [matAutocomplete]="individualAuto"
                        [matChipInputFor]="companyList"
                        [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                        (matChipInputTokenEnd)="addCompany($event)"
                      />
                    }
                  </mat-chip-grid>
                </div>
                <mat-autocomplete #companyAuto="matAutocomplete" (optionSelected)="selectedCompany($event)">
                  @for (company of filteredCompanies | async; track company) {
                    <mat-option [value]="company">{{ company.name }}</mat-option>
                  }
                </mat-autocomplete>
                <mat-autocomplete #individualAuto="matAutocomplete" (optionSelected)="selectedIndividual($event)">
                  @for (ind of filteredIndividuals | async; track ind) {
                    <mat-option [value]="ind">
                      {{ (ind.title ? ind.title + ' ' : '') + ind.first_name + ' ' + ind.last_name }}
                      ({{ getIndividualTypeLabel(ind) }})
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            </p>
          </div>

          <div class="col-md-6">
            <p>
              <mat-form-field appearance="outline">
                <mat-label>Phone number</mat-label>
                <input readonly formControlName="phone_number" type="text" matInput />
                @if (updateUserForm.get('phone_number')?.invalid) {
                  <mat-error>Phone number is invalid</mat-error>
                }
              </mat-form-field>
            </p>

            <p>
              <mat-form-field appearance="outline">
                <mat-label>Roles</mat-label>
                <mat-chip-grid #roleList aria-label="Role selection">
                  @for (role of roles; track role) {
                    <mat-chip-row [removable]="removable" (removed)="remove(role)">
                      {{ role }}
                      @if (removable) {
                        <button matChipRemove type="button">
                          <mat-icon>cancel</mat-icon>
                        </button>
                      }
                    </mat-chip-row>
                  }
                  <input
                    #roleInput
                    placeholder="Add role..."
                    formControlName="roles"
                    [matAutocomplete]="auto"
                    [matChipInputFor]="roleList"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                    (matChipInputTokenEnd)="add($event)"
                  />
                </mat-chip-grid>
              </mat-form-field>
              <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
                @for (role of filteredRoles | async; track role) {
                  <mat-option [value]="role">{{ role }}</mat-option>
                }
              </mat-autocomplete>
            </p>
          </div>
        </div>

        <div class="user-view-card__footer">
          <button type="submit" mat-flat-button color="primary" class="user-view-card__btn--primary">
            <mat-icon>save</mat-icon>
            Save
          </button>
        </div>
      </form>
    }
  </mat-card>
</div>
